/**
 * The Back Roads map: New York State drawn like a road map, for a chosen year.
 *
 * NOT THE GLOBE, DELIBERATELY. The globe is built for a timed pinpoint on a
 * sphere with an imagery skin capped at zoom 4, where the whole state is
 * eighty pixels wide. This is a flat map of one state at zooms 5 to 11, and
 * it wants to look like the map in the glove box: shaded hills, green
 * lowlands and tan uplands, blue water, county lines, red roads,
 * black-and-white rails, and the Blue Line around the Adirondack Park.
 * Sharing a component would mean fighting every one of those choices.
 *
 * THE RELIEF IS THE GPU'S JOB. The terrain source is vendored Terrarium
 * elevation tiles under `/nyterrain`, and MapLibre draws both the hypsometric
 * tint (`color-relief`) and the hillshade from them per frame. Nothing is
 * pre-rendered, so the palette below can be tuned without a bake.
 *
 * THE YEAR IS A FILTER, NOT A DATASET. Every route, reservoir and town
 * carries the year it opened and, where it applies, the year it went. The map
 * for 1948 is the same geometry as the map for 1970 with different features
 * passing the filter. That is the whole trick, and it is honest because the
 * dates are.
 *
 * NO LABELS, WITH ONE EXCEPTION. A labelled answer is not a question, so the
 * map draws no names. The exception is a handful of anchor cities that are
 * never asked for and always shown, because somebody who cannot see where
 * Albany is cannot place anything else either. They are DOM markers rather
 * than a symbol layer, so the style still carries no glyphs.
 *
 * ZERO EXTERNAL HOSTS. Every tile and every source is a relative path on this
 * origin, and `transformRequest` refuses anything else.
 */

import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Map as MapLibreMap, Marker } from 'maplibre-gl';
import type { GeoJSONSource, LngLatBoundsLike, StyleSpecification } from 'maplibre-gl';
import type { Feature, FeatureCollection, LineString, Point } from 'geojson';
import type { LngLat } from '@geogame/shared';
import type { RouteMode } from '@geogame/data/backroads';

export interface BackRoadsMapData {
  readonly counties: FeatureCollection;
  readonly states: FeatureCollection;
  readonly water: FeatureCollection;
  readonly rivers: FeatureCollection;
  /** Absent in a bake older than the Blue Line. */
  readonly parks?: FeatureCollection;
}

/**
 * The detail layer: today's named lakes and ponds, named rivers, state and
 * county routes, and every settlement as a dot. Loaded after the map proper,
 * because it is a few megabytes, and drawn under the dated era routes.
 */
export interface BackRoadsDetail {
  readonly water: FeatureCollection;
  readonly rivers: FeatureCollection;
  readonly roads: FeatureCollection;
  readonly places: FeatureCollection;
}

export interface RouteLine {
  readonly id: string;
  readonly mode: RouteMode;
  readonly from: number;
  readonly to?: number;
  readonly line: readonly LngLat[];
}

export interface Anchor {
  readonly id: string;
  readonly name: string;
  readonly lngLat: LngLat;
}

/** A town on the map: a dot while it exists. */
export interface TownDot {
  readonly id: string;
  readonly lngLat: LngLat;
  readonly from?: number;
  readonly to?: number;
  /** Bigger dot for a city, smaller for a hamlet. */
  readonly size: 1 | 2 | 3;
}

export interface BackRoadsMapProps {
  /** Null while the geometry is still loading; the map draws the relief and waits. */
  readonly data: BackRoadsMapData | null;
  /** Null until the big file lands; the map fills in when it does. */
  readonly detail: BackRoadsDetail | null;
  readonly year: number;
  readonly routes: readonly RouteLine[];
  readonly anchors: readonly Anchor[];
  readonly towns: readonly TownDot[];
  /** The stops already made on this trip, in order. Drawn as a trail. */
  readonly visited: readonly LngLat[];
  readonly youPin: LngLat | null;
  readonly targetPin: LngLat | null;
  readonly interactive: boolean;
  readonly onTap: (lngLat: LngLat) => void;
  readonly className?: string;
}

export interface BackRoadsMapHandle {
  fitBounds(bbox: readonly [number, number, number, number], durationMs?: number): void;
  zoomBy(delta: number): void;
}

const PAPER = {
  ground: '#dfe6d2',
  neighbour: '#d9d3c4',
  water: '#8dbbe0',
  waterEdge: '#5a8fbd',
  river: '#5a8fbd',
  county: '#8f7a59',
  stateLine: '#4a3a2a',
  park: '#1f5fae',
  canal: '#1f5fae',
  railCasing: '#221c15',
  railDash: '#fbf6ea',
  roadCasing: '#fff7e6',
  road: '#c8321f',
  roadCasingDetail: '#8f7f66',
  roadDetail: '#fffbf0',
  settlement: '#5a4a38',
  expresswayCasing: '#7a1a10',
  expressway: '#f0603f',
  town: '#3a2e22',
  townEdge: '#fbf6ea',
  trail: '#2f2f2f',
  visitedEdge: '#fbf6ea',
  you: '#1d4ed8',
  target: '#d97706',
  pinStroke: '#ffffff',
  anchor: '#221c15',
  shadow: '#4f3f2b',
  highlight: '#fffdf5',
  accent: '#7d6448',
} as const;

const SOURCE = {
  terrain: 'br-terrain',
  states: 'br-states',
  counties: 'br-counties',
  water: 'br-water',
  rivers: 'br-rivers',
  parks: 'br-parks',
  lakes: 'br-lakes',
  streams: 'br-streams',
  roads: 'br-roads',
  places: 'br-places',
  routes: 'br-routes',
  towns: 'br-towns',
  trail: 'br-trail',
  visited: 'br-visited',
  guess: 'br-guess',
  pins: 'br-pins',
  anchors: 'br-anchors',
} as const;

const LAYER = {
  relief: 'br-relief',
  hillshade: 'br-hillshade',
  neighbourFill: 'br-neighbour-fill',
  waterFill: 'br-water-fill',
  waterLine: 'br-water-line',
  rivers: 'br-rivers',
  countyLine: 'br-county-line',
  parkFill: 'br-park-fill',
  parkLine: 'br-park-line',
  lakesFill: 'br-lakes-fill',
  lakesLine: 'br-lakes-line',
  streams: 'br-streams',
  roadsCasing: 'br-roads-casing',
  roads: 'br-roads',
  places: 'br-places',
  stateLine: 'br-state-line',
  canal: 'br-route-water',
  railCasing: 'br-route-rail-casing',
  railDash: 'br-route-rail-dash',
  roadCasing: 'br-route-road-casing',
  road: 'br-route-road',
  expresswayCasing: 'br-route-expressway-casing',
  expressway: 'br-route-expressway',
  towns: 'br-towns',
  trail: 'br-trail',
  visited: 'br-visited',
  guess: 'br-guess',
  pinTarget: 'br-pin-target',
  pinYou: 'br-pin-you',
  anchors: 'br-anchors',
} as const;

const EMPTY: FeatureCollection = { type: 'FeatureCollection', features: [] };

/** The state, with room to pan a little past the border. */
const MAX_BOUNDS: LngLatBoundsLike = [
  [-82.5, 39.3],
  [-69.0, 46.4],
];
const HOME_BOUNDS: readonly [number, number, number, number] = [-79.9, 40.45, -71.75, 45.05];
/** What the vendored elevation tiles cover. Nothing is requested outside it. */
const TERRAIN_BOUNDS: [number, number, number, number] = [-80.1, 40.4, -71.6, 45.15];
const TERRAIN_MAX_ZOOM = 9;
const MIN_ZOOM = 5.2;
const MAX_ZOOM = 11;

/** Which of the dated features are on the map in `year`. */
function yearFilter(year: number): unknown[] {
  return [
    'all',
    ['<=', ['coalesce', ['get', 'from'], 0], year],
    ['>', ['coalesce', ['get', 'to'], 9999], year],
  ];
}

function modeFilter(mode: RouteMode, year: number): unknown[] {
  return ['all', ['==', ['get', 'mode'], mode], yearFilter(year)];
}

/**
 * Green in the valleys, tan on the plateaus, a paler wash on the High Peaks
 * where the trees give out. Elevations in metres; Marcy is 1,629.
 */
const RELIEF_RAMP: unknown = [
  'interpolate',
  ['linear'],
  ['elevation'],
  0,
  '#cfe0bd',
  120,
  '#d8e4bb',
  260,
  '#e3e5b8',
  420,
  '#e8dcaa',
  650,
  '#dcc493',
  900,
  '#c9a878',
  1200,
  '#b89469',
  1500,
  '#d9cdbb',
  1650,
  '#efe9df',
];

function buildStyle(): StyleSpecification {
  const yf = yearFilter(1900) as never;
  const routeFilter = (mode: RouteMode): never => modeFilter(mode, 1900) as never;
  const zoomWidth = (lo: number, hi: number): never =>
    ['interpolate', ['linear'], ['zoom'], 5, lo, 9, hi] as never;
  return {
    version: 8,
    // No glyphs, no sprite, no symbol layer: nothing here can write a name.
    sources: {
      [SOURCE.terrain]: {
        type: 'raster-dem',
        tiles: ['/nyterrain/{z}/{x}/{y}.png'],
        encoding: 'terrarium',
        tileSize: 256,
        minzoom: 5,
        maxzoom: TERRAIN_MAX_ZOOM,
        bounds: TERRAIN_BOUNDS,
      },
      [SOURCE.states]: { type: 'geojson', data: EMPTY },
      [SOURCE.counties]: { type: 'geojson', data: EMPTY },
      [SOURCE.water]: { type: 'geojson', data: EMPTY },
      [SOURCE.rivers]: { type: 'geojson', data: EMPTY },
      [SOURCE.parks]: { type: 'geojson', data: EMPTY },
      [SOURCE.lakes]: { type: 'geojson', data: EMPTY },
      [SOURCE.streams]: { type: 'geojson', data: EMPTY },
      [SOURCE.roads]: { type: 'geojson', data: EMPTY },
      [SOURCE.places]: { type: 'geojson', data: EMPTY },
      [SOURCE.routes]: { type: 'geojson', data: EMPTY },
      [SOURCE.towns]: { type: 'geojson', data: EMPTY },
      [SOURCE.trail]: { type: 'geojson', data: EMPTY },
      [SOURCE.visited]: { type: 'geojson', data: EMPTY },
      [SOURCE.guess]: { type: 'geojson', data: EMPTY },
      [SOURCE.pins]: { type: 'geojson', data: EMPTY },
      [SOURCE.anchors]: { type: 'geojson', data: EMPTY },
    },
    layers: [
      { id: 'br-ground', type: 'background', paint: { 'background-color': PAPER.ground } },
      {
        id: LAYER.relief,
        type: 'color-relief',
        source: SOURCE.terrain,
        paint: { 'color-relief-color': RELIEF_RAMP as never, 'color-relief-opacity': 1 },
      } as never,
      {
        id: LAYER.hillshade,
        type: 'hillshade',
        source: SOURCE.terrain,
        paint: {
          'hillshade-exaggeration': 0.5,
          'hillshade-shadow-color': PAPER.shadow,
          'hillshade-highlight-color': PAPER.highlight,
          'hillshade-accent-color': PAPER.accent,
          'hillshade-illumination-direction': 335,
        },
      },
      // The neighbours, greyed over the relief so New York is the map.
      {
        id: LAYER.neighbourFill,
        type: 'fill',
        source: SOURCE.states,
        filter: ['!=', ['get', 'ny'], true],
        paint: { 'fill-color': PAPER.neighbour, 'fill-opacity': 0.72 },
      },
      {
        id: LAYER.countyLine,
        type: 'line',
        source: SOURCE.counties,
        paint: {
          'line-color': PAPER.county,
          'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.5, 8, 1, 11, 1.6],
          'line-dasharray': [3, 2],
          'line-opacity': 0.8,
        },
      },
      {
        id: LAYER.waterFill,
        type: 'fill',
        source: SOURCE.water,
        filter: yf,
        paint: { 'fill-color': PAPER.water },
      },
      {
        id: LAYER.waterLine,
        type: 'line',
        source: SOURCE.water,
        filter: yf,
        paint: { 'line-color': PAPER.waterEdge, 'line-width': 0.9 },
      },
      {
        id: LAYER.rivers,
        type: 'line',
        source: SOURCE.rivers,
        paint: {
          'line-color': PAPER.river,
          'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.9, 9, 2, 11, 2.8],
        },
      },
      // The detail layer: today's ponds and streams, under everything dated.
      {
        id: LAYER.lakesFill,
        type: 'fill',
        source: SOURCE.lakes,
        paint: { 'fill-color': PAPER.water },
      },
      {
        id: LAYER.lakesLine,
        type: 'line',
        source: SOURCE.lakes,
        paint: { 'line-color': PAPER.waterEdge, 'line-width': ['interpolate', ['linear'], ['zoom'], 6, 0.4, 10, 1] },
      },
      {
        id: LAYER.streams,
        type: 'line',
        source: SOURCE.streams,
        paint: {
          'line-color': PAPER.river,
          'line-width': ['interpolate', ['linear'], ['zoom'], 6, 0.5, 9, 1.3, 11, 2],
          'line-opacity': ['interpolate', ['linear'], ['zoom'], 6, 0.35, 8, 0.9],
        },
      },
      {
        id: LAYER.parkFill,
        type: 'fill',
        source: SOURCE.parks,
        paint: { 'fill-color': PAPER.park, 'fill-opacity': 0.05 },
      },
      {
        id: LAYER.parkLine,
        type: 'line',
        source: SOURCE.parks,
        layout: { 'line-join': 'round' },
        paint: {
          'line-color': PAPER.park,
          'line-width': ['interpolate', ['linear'], ['zoom'], 5, 1.4, 9, 2.6],
          'line-opacity': 0.9,
        },
      },
      {
        id: LAYER.stateLine,
        type: 'line',
        source: SOURCE.states,
        paint: {
          'line-color': PAPER.stateLine,
          'line-width': ['interpolate', ['linear'], ['zoom'], 5, 1.2, 11, 2.4],
        },
      },
      // Today's state and county routes: a light double line, the way a road
      // map draws the roads it does not number in red.
      {
        id: LAYER.roadsCasing,
        type: 'line',
        source: SOURCE.roads,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': PAPER.roadCasingDetail,
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            6,
            ['case', ['==', ['get', 'c'], 1], 1.8, 1.2],
            9,
            ['case', ['==', ['get', 'c'], 1], 3.8, 2.8],
            11,
            ['case', ['==', ['get', 'c'], 1], 5.6, 4.2],
          ],
          'line-opacity': ['interpolate', ['linear'], ['zoom'], 6, 0.45, 7.5, 1],
        },
      },
      {
        id: LAYER.roads,
        type: 'line',
        source: SOURCE.roads,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': PAPER.roadDetail,
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            6,
            ['case', ['==', ['get', 'c'], 1], 0.7, 0.4],
            9,
            ['case', ['==', ['get', 'c'], 1], 1.8, 1.1],
            11,
            ['case', ['==', ['get', 'c'], 1], 3, 2],
          ],
          'line-opacity': ['interpolate', ['linear'], ['zoom'], 6, 0.3, 7.5, 1],
        },
      },
      {
        id: LAYER.canal,
        type: 'line',
        source: SOURCE.routes,
        filter: routeFilter('water'),
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': PAPER.canal, 'line-width': zoomWidth(2, 3.6), 'line-dasharray': [2, 1.2] },
      },
      {
        id: LAYER.railCasing,
        type: 'line',
        source: SOURCE.routes,
        filter: routeFilter('rail'),
        layout: { 'line-join': 'round' },
        paint: { 'line-color': PAPER.railCasing, 'line-width': zoomWidth(1.8, 3.4) },
      },
      {
        id: LAYER.railDash,
        type: 'line',
        source: SOURCE.routes,
        filter: routeFilter('rail'),
        layout: { 'line-join': 'round' },
        paint: { 'line-color': PAPER.railDash, 'line-width': zoomWidth(1, 2), 'line-dasharray': [3, 3] },
      },
      {
        id: LAYER.roadCasing,
        type: 'line',
        source: SOURCE.routes,
        filter: routeFilter('road'),
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': PAPER.roadCasing, 'line-width': zoomWidth(3.2, 5.6), 'line-opacity': 0.85 },
      },
      {
        id: LAYER.road,
        type: 'line',
        source: SOURCE.routes,
        filter: routeFilter('road'),
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': PAPER.road, 'line-width': zoomWidth(1.7, 3.2) },
      },
      {
        id: LAYER.expresswayCasing,
        type: 'line',
        source: SOURCE.routes,
        filter: routeFilter('expressway'),
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': PAPER.expresswayCasing, 'line-width': zoomWidth(3.8, 7) },
      },
      {
        id: LAYER.expressway,
        type: 'line',
        source: SOURCE.routes,
        filter: routeFilter('expressway'),
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': PAPER.expressway, 'line-width': zoomWidth(2.1, 4) },
      },
      // Every settlement as a small dot: villages from zoom 7, hamlets from 8.5.
      {
        id: LAYER.places,
        type: 'circle',
        source: SOURCE.places,
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            7,
            ['case', ['>=', ['get', 's'], 2], 1.6, 0],
            9,
            ['case', ['>=', ['get', 's'], 2], 2.8, 1.6],
            11,
            ['case', ['>=', ['get', 's'], 2], 4, 2.6],
          ],
          'circle-color': PAPER.settlement,
          'circle-stroke-color': PAPER.townEdge,
          'circle-stroke-width': 0.8,
          'circle-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            7,
            ['case', ['>=', ['get', 's'], 2], 0.6, 0],
            8.5,
            ['case', ['>=', ['get', 's'], 2], 0.9, 0.6],
          ],
          'circle-stroke-opacity': ['interpolate', ['linear'], ['zoom'], 7, 0, 8.5, 0.9],
        },
      },
      {
        id: LAYER.towns,
        type: 'circle',
        source: SOURCE.towns,
        filter: yf,
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            5,
            ['*', ['get', 'size'], 0.7],
            8,
            ['*', ['get', 'size'], 1.4],
            11,
            ['*', ['get', 'size'], 2.2],
          ],
          'circle-color': PAPER.town,
          'circle-stroke-color': PAPER.townEdge,
          'circle-stroke-width': 1,
          'circle-opacity': ['interpolate', ['linear'], ['zoom'], 5.5, 0, 6.5, 1],
          'circle-stroke-opacity': ['interpolate', ['linear'], ['zoom'], 5.5, 0, 6.5, 1],
        },
      },
      {
        id: LAYER.trail,
        type: 'line',
        source: SOURCE.trail,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': PAPER.trail, 'line-width': 2, 'line-dasharray': [1, 2] },
      },
      {
        id: LAYER.visited,
        type: 'circle',
        source: SOURCE.visited,
        paint: {
          'circle-radius': 6,
          'circle-color': PAPER.trail,
          'circle-stroke-color': PAPER.visitedEdge,
          'circle-stroke-width': 2,
        },
      },
      {
        id: LAYER.anchors,
        type: 'circle',
        source: SOURCE.anchors,
        paint: {
          'circle-radius': 4.5,
          'circle-color': PAPER.anchor,
          'circle-stroke-color': PAPER.townEdge,
          'circle-stroke-width': 1.5,
        },
      },
      {
        id: LAYER.guess,
        type: 'line',
        source: SOURCE.guess,
        paint: { 'line-color': PAPER.you, 'line-width': 2, 'line-dasharray': [2, 2] },
      },
      {
        id: LAYER.pinTarget,
        type: 'circle',
        source: SOURCE.pins,
        filter: ['==', ['get', 'kind'], 'target'],
        paint: {
          'circle-radius': 10,
          'circle-color': PAPER.target,
          'circle-stroke-color': PAPER.pinStroke,
          'circle-stroke-width': 3,
        },
      },
      {
        id: LAYER.pinYou,
        type: 'circle',
        source: SOURCE.pins,
        filter: ['==', ['get', 'kind'], 'you'],
        paint: {
          'circle-radius': 10,
          'circle-color': PAPER.you,
          'circle-stroke-color': PAPER.pinStroke,
          'circle-stroke-width': 3,
        },
      },
    ],
  };
}

function pointFeature(lngLat: LngLat, props: Record<string, unknown>): Feature<Point> {
  return { type: 'Feature', properties: props, geometry: { type: 'Point', coordinates: [lngLat[0], lngLat[1]] } };
}

function lineFeature(line: readonly LngLat[], props: Record<string, unknown>): Feature<LineString> {
  return {
    type: 'Feature',
    properties: props,
    geometry: { type: 'LineString', coordinates: line.map((p) => [p[0], p[1]]) },
  };
}

function setSource(map: MapLibreMap, id: string, data: FeatureCollection): void {
  const source = map.getSource<GeoJSONSource>(id);
  if (source) source.setData(data);
}

function toBounds(bbox: readonly [number, number, number, number]): LngLatBoundsLike {
  return [
    [bbox[0], bbox[1]],
    [bbox[2], bbox[3]],
  ];
}

function dated(props: Record<string, unknown>, from: number | undefined, to: number | undefined): Record<string, unknown> {
  const out = { ...props };
  if (from !== undefined) out['from'] = from;
  if (to !== undefined) out['to'] = to;
  return out;
}

export const BackRoadsMap: React.ForwardRefExoticComponent<
  BackRoadsMapProps & React.RefAttributes<BackRoadsMapHandle>
> = forwardRef<BackRoadsMapHandle, BackRoadsMapProps>(function BackRoadsMap(props, ref) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const loadedRef = useRef(false);
  const markersRef = useRef<Marker[]>([]);
  const tapRef = useRef(props.onTap);
  tapRef.current = props.onTap;
  const interactiveRef = useRef(props.interactive);
  interactiveRef.current = props.interactive;
  /** Work queued while the style loads. Each entry runs once, in order. */
  const pendingRef = useRef<Array<(map: MapLibreMap) => void>>([]);

  // Create the map once. Everything after is an effect writing into it.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const map = new MapLibreMap({
      container,
      style: buildStyle(),
      bounds: toBounds(HOME_BOUNDS),
      fitBoundsOptions: { padding: 12 },
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      maxBounds: MAX_BOUNDS,
      maxPitch: 0,
      dragRotate: false,
      pitchWithRotate: false,
      rollEnabled: false,
      touchPitch: false,
      renderWorldCopies: false,
      attributionControl: false,
      maplibreLogo: false,
      fadeDuration: 0,
      // Committed build artefacts. Never revalidate them.
      refreshExpiredTiles: false,
      pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
      transformRequest: (url: string) => {
        if (url.includes('://') && !url.startsWith(window.location.origin)) {
          throw new Error(`[backroads] refusing off-origin request: ${url}`);
        }
        return { url };
      },
    });
    map.keyboard.disableRotation();
    map.touchZoomRotate.disableRotation();

    map.on('click', (e) => {
      if (!interactiveRef.current) return;
      tapRef.current([e.lngLat.lng, e.lngLat.lat]);
    });
    const pending = pendingRef.current;
    map.on('load', () => {
      loadedRef.current = true;
      map.resize();
      for (const fn of pending.splice(0)) fn(map);
    });

    mapRef.current = map;
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(() => map.resize()) : null;
    observer?.observe(container);

    return () => {
      observer?.disconnect();
      for (const m of markersRef.current) m.remove();
      markersRef.current = [];
      loadedRef.current = false;
      mapRef.current = null;
      map.remove();
    };
  }, []);

  function whenReady(fn: (map: MapLibreMap) => void): void {
    const map = mapRef.current;
    if (map && loadedRef.current) fn(map);
    else pendingRef.current.push(fn);
  }

  // Geometry.
  useEffect(() => {
    const data = props.data;
    if (!data) return;
    whenReady((map) => {
      setSource(map, SOURCE.states, data.states);
      setSource(map, SOURCE.counties, data.counties);
      setSource(map, SOURCE.water, data.water);
      setSource(map, SOURCE.rivers, data.rivers);
      setSource(map, SOURCE.parks, data.parks ?? EMPTY);
    });
  }, [props.data]);

  // Routes, as one source with a mode and dates per feature.
  useEffect(() => {
    const routes = props.routes;
    whenReady((map) => {
      setSource(map, SOURCE.routes, {
        type: 'FeatureCollection',
        features: routes.map((r) => lineFeature(r.line, dated({ id: r.id, mode: r.mode }, r.from, r.to))),
      });
    });
  }, [props.routes]);

  // Towns: a dot while the place exists.
  useEffect(() => {
    const towns = props.towns;
    whenReady((map) => {
      setSource(map, SOURCE.towns, {
        type: 'FeatureCollection',
        features: towns.map((t) => pointFeature(t.lngLat, dated({ id: t.id, size: t.size }, t.from, t.to))),
      });
    });
  }, [props.towns]);

  // The detail layer, whenever it lands.
  useEffect(() => {
    const detail = props.detail;
    if (!detail) return;
    whenReady((map) => {
      setSource(map, SOURCE.lakes, detail.water);
      setSource(map, SOURCE.streams, detail.rivers);
      setSource(map, SOURCE.roads, detail.roads);
      setSource(map, SOURCE.places, detail.places);
    });
  }, [props.detail]);

  // The year: a filter on every dated layer.
  useEffect(() => {
    const year = props.year;
    whenReady((map) => {
      map.setFilter(LAYER.waterFill, yearFilter(year) as never);
      map.setFilter(LAYER.waterLine, yearFilter(year) as never);
      map.setFilter(LAYER.towns, yearFilter(year) as never);
      map.setFilter(LAYER.canal, modeFilter('water', year) as never);
      map.setFilter(LAYER.railCasing, modeFilter('rail', year) as never);
      map.setFilter(LAYER.railDash, modeFilter('rail', year) as never);
      map.setFilter(LAYER.roadCasing, modeFilter('road', year) as never);
      map.setFilter(LAYER.road, modeFilter('road', year) as never);
      map.setFilter(LAYER.expresswayCasing, modeFilter('expressway', year) as never);
      map.setFilter(LAYER.expressway, modeFilter('expressway', year) as never);
    });
  }, [props.year]);

  // Anchors: a dot on the style and a name in the DOM.
  useEffect(() => {
    const anchors = props.anchors;
    whenReady((map) => {
      setSource(map, SOURCE.anchors, {
        type: 'FeatureCollection',
        features: anchors.map((a) => pointFeature(a.lngLat, { id: a.id })),
      });
      for (const m of markersRef.current) m.remove();
      markersRef.current = anchors.map((a) => {
        const el = document.createElement('div');
        el.className = 'gg-br-label';
        el.textContent = a.name;
        // The label sits up and to the right of the dot, clear of the tap.
        return new Marker({ element: el, anchor: 'bottom-left', offset: [5, -4] })
          .setLngLat([a.lngLat[0], a.lngLat[1]])
          .addTo(map);
      });
    });
  }, [props.anchors]);

  // The trail of stops already made.
  useEffect(() => {
    const visited = props.visited;
    whenReady((map) => {
      setSource(map, SOURCE.visited, {
        type: 'FeatureCollection',
        features: visited.map((p, i) => pointFeature(p, { i })),
      });
      setSource(map, SOURCE.trail, {
        type: 'FeatureCollection',
        features: visited.length >= 2 ? [lineFeature(visited, {})] : [],
      });
    });
  }, [props.visited]);

  // Pins and the line between them.
  useEffect(() => {
    const you = props.youPin;
    const target = props.targetPin;
    whenReady((map) => {
      const features: Feature<Point>[] = [];
      if (target) features.push(pointFeature(target, { kind: 'target' }));
      if (you) features.push(pointFeature(you, { kind: 'you' }));
      setSource(map, SOURCE.pins, { type: 'FeatureCollection', features });
      setSource(map, SOURCE.guess, {
        type: 'FeatureCollection',
        features: you && target ? [lineFeature([you, target], {})] : [],
      });
    });
  }, [props.youPin, props.targetPin]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.getCanvas().style.cursor = props.interactive ? 'crosshair' : '';
  }, [props.interactive]);

  useImperativeHandle(
    ref,
    () => ({
      fitBounds(bbox, durationMs = 700) {
        whenReady((map) => {
          map.fitBounds(toBounds(bbox), { padding: 24, duration: durationMs, maxZoom: MAX_ZOOM });
        });
      },
      zoomBy(delta) {
        whenReady((map) => {
          map.zoomTo(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, map.getZoom() + delta)), { duration: 350 });
        });
      },
    }),
    [],
  );

  return <div ref={containerRef} className={props.className ?? 'gg-br-map'} role="application" aria-label="Map of New York State" />;
});
